/**
 * The collapsible "Enquiry details" card (prototype `enquiryHeader`).
 *
 * ---- CORRECTION 2026-08-04: Task 7's "most of this card lost its write path" was largely wrong ----
 *
 * Task 7 modelled `enquiry.get_enquiry` from the Postman collection's ABBREVIATED example
 * response, not a live call, and concluded only `customer`/`enquiry_type`/`purchaser` overlapped
 * this card and that `update_enquiry`'s writable body was `{status, remarks_to_supplier, items}`
 * only. Both were re-measured against `rfqdev.etoserp.com` (ENQ-2026-00004): the live response
 * carries `sales_person`, `vessel`, `delivery_location`, `port_of_delivery`, `use_email_body` and
 * `transaction_date` too (see `useEnquiries.ts`'s module header for the full field list), and
 * `update_enquiry` accepts writes to fields well outside its documented body — confirmed here for
 * one representative field per Frappe fieldtype (`delivery_location`/Link, `customer_reference_no`
 * /Data, `transaction_date`/Date, `use_email_body`/Check, `enquiry_type`/Select), every value
 * restored immediately after, same finding the user's own independent `priority` probe reports.
 *
 * So this card now has three real categories, not the one blanket "disabled" it had before:
 *
 * 1. Genuinely absent from the wire — `subject`, `sender`, `company_series`, `department`. No live
 *    field exists for any of these; shown disabled with `NOT_ON_WIRE`. (`company` was listed here
 *    until 2026-08-08 and does not belong: it is a real field, is returned, and — measured — is
 *    writable. See category 2.)
 * 2. On the wire, editable, but going through a Link fieldtype server-side — `Customer`,
 *    `Company`, `Sales Person`, `Vessel`, `Delivery Location`, `Port of Delivery`. A free-text edit here
 *    fails Frappe's Link validation on save (exactly the bug commit `2d803ae` — "Link fields are
 *    pickers, not free text" — already fixed for the sibling RFQ item table), so these use the
 *    same `LinkCombo` picker that fix introduced, backed by the picker hooks already sitting in
 *    `useEnquiryFromMail.ts` (`useVesselPicker`, `useDeliveryLocationPicker`,
 *    `usePortPicker`). `Sales Person` is the exception: it reads `useMasters.ts`'s
 *    `useSalesPeople`, which scopes the list to this enquiry's customer AND company via
 *    `sales_person.get_salesperson` (fix list point 4) instead of offering the whole master. `Customer` was free text even in the
 *    pre-Task-7 version of this card — that was itself the same class of bug, just never hit
 *    because nobody had reason to retype a customer that was already correct; not carried
 *    forward here.
 * 3. On the wire, editable, plain scalar — `Sales Person`'s Data-typed sibling fields would fall
 *    here too, but this card has none of its own beyond what's already covered by #2 above;
 *    `Enquiry Type` (Select → dropdown, restricted to `Provision`/`Technical` — see
 *    `toEnquiryType` in `useEnquiries.ts` for why the other two `EnquiryType` values aren't
 *    offered) is the one non-Link writable field this card has.
 *    `Date` is on the wire (`transaction_date`) and shown/edited as plain text, same as before.
 *
 *    `Use Email Body` (a `Check` on the wire) USED to sit here as a checkbox and was removed
 *    2026-08-06 at the user's request. It steered extraction — body text vs attachment only — a
 *    decision that belongs to the mail screen where the extraction is actually run, not to a
 *    header card the purchaser opens afterwards, by which point the extraction has already
 *    happened and toggling it here changes nothing they can see. The field is untouched on the
 *    wire and still round-trips through `toEnquiryHeader`; only the control is gone.
 *
 * `Type` ('Auto'/'Manual') is shown with its real derived value (see `toEnquiryHeader`) but stays
 * READ-ONLY: there's no `type` field on the wire to write to (it's derived from `source`, a
 * different name), and writing `source` itself was not part of what this fix verified.
 *
 * Every write goes through `useUpdateEnquiry` (`enquiry.update_enquiry`) via the shared `patch`
 * helper below. `Purchaser` keeps its own separate `assign_purchaser` endpoint, unchanged.
 */

import { useEffect, useRef, useState } from 'react'

import { LinkCombo } from '../../components/LinkPicker'
import { FALLBACK_COMPANY } from '../../lib/api/company'
import { useCompanyPicker, useCustomerCurrency, useCustomerDepartments, useDeliveryLocationPriceLists, useSalesPeople } from '../../lib/api/useMasters'
import { baseCurrencyFor } from '../../lib/api/baseCurrency'
import { useCustomerSearch } from '../../lib/api/useCustomerRecord'
import { describeMandatory, enquiryHeaderGaps } from '../../lib/quotationMandatory'
import type { PickerOption } from '../../lib/api/useEnquiryFromMail'
import {
  useCurrencyPicker,
  useDeliveryLocationPicker,
  usePortPicker,
  usePurchaserPicker,
  useVesselPicker,
  useVesselName,
} from '../../lib/api/useEnquiryFromMail'
import { useSession } from '../../components/SessionProvider'
import { useAssignPurchaser, useUpdateEnquiry, type UpdateEnquiryPatch } from '../../lib/api/useEnquiryMutations'
import { useQuotationNameForEnquiry, useUpdateQuotationTerms } from '../../lib/api/useQuotation'
import { SalesPersonDefaulter } from './SalesPersonDefaulter'
import { useToast } from '../../components/Toast'
import type { Enquiry } from '../../lib/types'

/** Shown on the five fields this card genuinely has no wire source for at all — see the module
 * header's category 1. */

/** The two `enquiry_type` values this fix actually confirmed the live Select accepts — see the
 * module header. `Commercial`/`Bonded Stores` are real members of Bosun's own `EnquiryType`
 * domain but unconfirmed against the server's real option list, so they aren't offered here. */
const WRITABLE_ENQUIRY_TYPES = ['Provision', 'Technical'] as const

/** A text field that commits on blur, so typing doesn't fire a request per keystroke. Also used,
 * with `disabled`, for a field this card can only ever show, never edit. */
function TextField({
  label,
  value,
  onCommit,
  placeholder,
  disabled,
  hint,
  type = 'text',
}: {
  label: string
  value: string
  onCommit?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  hint?: string
  /** `date` for a `YYYY-MM-DD` field — the browser's picker, and the wire format for free. */
  type?: 'text' | 'date'
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])

  return (
    <label className="ef">
      <span>{label}</span>
      <input
        type={type}
        value={draft}
        placeholder={placeholder}
        disabled={disabled || !onCommit}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (onCommit && draft !== value) onCommit(draft)
        }}
      />
      {hint ? <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>{hint}</div> : null}
    </label>
  )
}

/**
 * A Link field in this card's `.ef` layout — see the module header's category 2 for why a plain
 * text input cannot work for these: Frappe validates a Link on save, so a typed value that isn't
 * an existing docname is rejected after the user has already moved on.
 */
function LinkField({
  label,
  value,
  useOptions,
  placeholder,
  onCommit,
  guessed,
  hint,
  tag,
}: {
  label: string
  value: string
  useOptions: (search: string) => { data?: PickerOption[]; isPending: boolean }
  placeholder: string
  onCommit: (value: string) => void
  /** Filled in by a guess from the mail rather than by a person — see `CreateEnquiryBar.tsx`,
   * which resolves customer and delivery location from the letterhead and the body text. Cleared
   * the moment the field is committed, because then it is no longer a guess. */
  guessed?: boolean
  /** A consequence of the CURRENT value worth saying out loud — see `Company`'s empty case, where
   * blank silently means "RFQs go out as somebody else". Rendered below `guessed`, never instead
   * of it. */
  hint?: string
  /** A fact the value implies, shown as a chip — the price list a delivery location carries. */
  tag?: string
}) {
  return (
    <label className="ef">
      <span>{label}</span>
      <LinkCombo value={value} onChange={onCommit} useOptions={useOptions} placeholder={placeholder} />
      {tag ? (
        <div style={{ marginTop: 4 }}>
          <span className="badge" style={{ background: 'var(--bg)', color: 'var(--ink)', fontSize: 10.5 }}>
            {tag}
          </span>
        </div>
      ) : null}
      {guessed ? (
        <div style={{ fontSize: 10.5, color: 'var(--warn)', marginTop: 3 }}>guessed from the mail — confirm</div>
      ) : null}
      {hint ? <div style={{ fontSize: 10.5, color: 'var(--warn)', marginTop: 3 }}>{hint}</div> : null}
    </label>
  )
}

function SelectField({
  label,
  value,
  options,
  onCommit,
  disabled,
  hint,
}: {
  label: string
  value: string
  options: readonly string[]
  /** Omitted for a read-only select — see `Department`, whose options are real but unsavable. */
  onCommit?: (value: string) => void
  disabled?: boolean
  hint?: string
}) {
  return (
    <label className="ef">
      <span>{label}</span>
      <select
        value={value}
        disabled={disabled || onCommit === undefined}
        onChange={(e) => onCommit?.(e.target.value)}
      >
        {/* A disabled select whose value is '' would otherwise render the FIRST option, which
            reads as a department nobody chose. */}
        {value === '' ? <option value="">—</option> : null}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      {hint ? <small>{hint}</small> : null}
    </label>
  )
}

export function EnquiryHeaderCard({ enquiry }: { enquiry: Enquiry }) {
  /**
   * Which guessed fields the purchaser has confirmed THIS session.
   *
   * Deliberately NOT persisted: there is no server field recording that a value was guessed, so a
   * reload clears the flag — which is the honest behaviour, because at that point we genuinely no
   * longer know. The flag is a prompt to check on arrival, not permanent state.
   */
  const [confirmed, setConfirmed] = useState<Set<string>>(() => new Set())
  // `EnquiryHeader` carries `mail_id: string | null` and no `source` field — a non-empty mail_id
  // IS the "created from a mail, so these two were guessed" signal.
  const wasGuessed = (field: string) => !!enquiry.header.mail_id && !confirmed.has(field)
  const confirm = (field: string) => setConfirmed((prev) => new Set(prev).add(field))

  const [open, setOpen] = useState(true)
  const { isManager } = useSession()
  const toast = useToast()
  const header = enquiry.header

  /*
    The sales-person picker is scoped to THIS enquiry's customer and company (fix list point 4).

    `LinkField` takes a hook, so the two filter values are closed over here — where they are
    already held — rather than threaded through as props. `useCallback` keeps the identity stable
    across renders for the same pair; the closure is still called unconditionally, in the same
    position, on every render, so it is a hook call like any other.

    `useSalesPeople` falls back to the unfiltered master when either half is missing — see its
    own header for why an empty list would be the wrong answer there.
  */
  const useScopedSalesPeople = (search: string) => useSalesPeople(header.customer, header.company, search)
  // Customers of THIS enquiry's company — `get_customers` needs the company (Akhil, 5d), and the
  // unscoped resource read it replaced listed every customer in the ERP.
  const headerGaps = enquiryHeaderGaps(header)
  const useScopedCustomers = (search: string) => useCustomerSearch(search, header.company)
  // Reloads whenever Enquiry Type changes above — that dependency is the whole feature.
  const departments = useCustomerDepartments(header.enquiry_type)
  const vesselName = useVesselName(header.vessel)
  /*
    What the location implies. The enquiry's own `price_list` wins where it is set — it is the
    record — and the map fills in for an enquiry whose location was chosen before this field was
    written, so the tag is never blank on the older ones.
  */
  const priceLists = useDeliveryLocationPriceLists()
  const impliedPriceList = header.delivery_location ? priceLists.data?.get(header.delivery_location) : undefined
  const shownPriceList = header.price_list ?? impliedPriceList ?? null
  /*
    The customer's currency defaults from the COMPANY (Akhil, 2026-09-14) — or from the customer's
    own currency on file where the master has one, since that is what the quotation build already
    used before this field existed and showing a different default here would contradict it. It
    is written on the first save; `null` on the header means "never chosen", and the card shows
    the default rather than a blank so the person filling it in sees what the quotation will be
    built in.
  */
  const customerCurrency = useCustomerCurrency(header.customer)
  const defaultCurrency = customerCurrency.data || baseCurrencyFor(header.company)
  const defaultCurrencySource = customerCurrency.data ? `${header.customer}'s currency on file` : header.company || 'the company'

  const updateEnquiry = useUpdateEnquiry(enquiry.id, {
    onError: (error) => toast(`Could not save: ${error.message}`),
  })  /*
    WRITTEN, not only shown (Akhil, 2026-09-17: "STANDARD SELLING PRICE is not updated on the
    enquiry side"). The map filled the tag on screen while the record stayed `null` — the price
    list was only written when the location was changed on this card, never for an enquiry that
    arrived with one. Once per enquiry, deferred off the mount tick like every automatic write.
  */
  const priceListFilledFor = useRef<string | null>(null)
  useEffect(() => {
    if (header.price_list || !impliedPriceList || updateEnquiry.isPending || priceListFilledFor.current === enquiry.id) return
    const timer = setTimeout(() => {
      priceListFilledFor.current = enquiry.id
      updateEnquiry.mutate({ price_list: impliedPriceList })
    }, 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header.price_list, impliedPriceList, enquiry.id, updateEnquiry.isPending])

  const assignPurchaser = useAssignPurchaser(enquiry.id, {
    onSuccess: (data) => toast(`Assigned to ${data.purchaser}`),
    onError: (error) => toast(error.status === 403 ? 'Only a purchase manager can reassign.' : error.message),
  })

  const patch = (fields: UpdateEnquiryPatch) => updateEnquiry.mutate(fields)

  /*
    Vessel, port and delivery location reach the EXISTING quotation too (Akhil, 2026-09-15: "if
    these fields are updated in the Enquiry, the changes are not reflected in the Quotation").
    The quotation copies them at build and never re-reads the enquiry, so a correction here used
    to stop at the enquiry. Written after the enquiry write succeeds, only when a quotation
    exists; a failure is reported and never undoes the enquiry change.
  */
  const existingQuotation = useQuotationNameForEnquiry(enquiry.id)
  const syncQuotation = useUpdateQuotationTerms(enquiry.id, existingQuotation.data?.name, {
    onError: (error) => toast(`Saved on the enquiry, but the quotation could not be updated: ${error.message}`),
  })
  const patchLogistics = (fields: Pick<UpdateEnquiryPatch, 'vessel' | 'delivery_location' | 'delivery_port' | 'price_list'>) => {
    updateEnquiry.mutate(fields, {
      onSuccess: () => {
        if (!existingQuotation.data?.name) return
        syncQuotation.mutate({
          ...(fields.vessel !== undefined ? { vessel: fields.vessel ?? '' } : {}),
          ...(fields.delivery_port !== undefined ? { port: fields.delivery_port ?? '' } : {}),
          ...(fields.delivery_location !== undefined ? { delivery_location: fields.delivery_location ?? '' } : {}),
        })
        toast(`Also updated on quotation ${existingQuotation.data.name}`)
      },
    })
  }

  return (
    <div className="card pad" style={{ marginBottom: 14 }}>
      {/*
        The sales person the server resolves for this customer + company, APPLIED rather than
        merely offered. It was being shown at the top of the picker marked "handles this customer"
        and left for the user to select by hand, on every enquiry. Writes only into an empty field,
        only when the lookup resolves exactly one person, and only once — see the component.
      */}
      <SalesPersonDefaulter
        enquiryId={enquiry.id}
        customer={header.customer}
        company={header.company}
        current={header.sales_person}
        enabled={!updateEnquiry.isPending}
        apply={async (sales_person: string) => {
          await updateEnquiry.mutateAsync({ sales_person })
          toast(`Sales person set to ${sales_person} — the one who handles this customer.`)
        }}
        // Never cleared any more (2026-09-18): the field is mandatory downstream.
        onKept={(kept) => toast(`Nobody is assigned to this customer — sales person ${kept} kept from before. Change it in Enquiry details if needed.`)}
      />
      <div
        id="enquiry-header-card"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        <b>
          📋 Enquiry details
          {header.subject ? (
            <span style={{ color: 'var(--muted)', fontWeight: 500 }}> · {header.subject}</span>
          ) : null}
        </b>
        <span style={{ color: 'var(--muted)' }}>{open ? '▲ collapse' : '▼ expand'}</span>
      </div>
      {/* What the QUOTATION will require and the enquiry cannot yet supply (2026-09-17). Shown
          open or closed, because the RFQ send refuses on exactly these. */}
      {headerGaps.length > 0 ? (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--warn)' }}>
          ⚠ Still needed before an RFQ can go and a quotation can be built: <b>{describeMandatory(headerGaps)}</b>.
        </div>
      ) : null}

      {open ? (
        <div className="hdrgrid">
          <TextField label="Date" value={header.date} onCommit={(date) => patch({ date })} />
          {/*
            EDITABLE as of 2026-08-08, and it matters more than it looks.

            `company` is a real `Link → Company` on `Enquiry` and `update_enquiry` PERSISTS it
            (probe in `useEnquiryMutations.ts`'s `UpdateEnquiryPatch.company`). Bosun is
            multi-company — `lookups.get_companies` returns seven entities across four currencies —
            and `create_enquiry` refuses the field (`Q7`), so every enquiry raised from a mail
            arrives with NO company. Until one is set, `create_rfq` is stamped with
            `company.ts`'s `FALLBACK_COMPANY`, i.e. one legal entity on documents that go to
            suppliers under six others. This picker is how that gets fixed, which is why the empty
            case says so out loud rather than just rendering blank.
          */}
          <LinkField
            label="Company"
            value={header.company}
            useOptions={useCompanyPicker}
            placeholder="Search companies…"
            onCommit={(company) => patch({ company })}
            hint={
              header.company === ''
                ? `not set — RFQs will go out as ${FALLBACK_COMPANY}`
                : undefined
            }
          />

          <LinkField
            label="Customer"
            value={header.customer}
            useOptions={useScopedCustomers}
            placeholder="Search customers…"
            guessed={wasGuessed('customer')}
            onCommit={(customer) => {
              confirm('customer')
              patch({ customer })
            }}
          />
          {/*
            The customer's own reference for this enquiry and its date — both persist through
            `update_enquiry` (probed 2026-09-14) and are what the quotation quotes back to them.
          */}
          <TextField
            label="Customer Ref No"
            value={header.customer_reference_no}
            placeholder="Their PO / enquiry number"
            onCommit={(v) => patch({ customer_reference_no: v.trim() === '' ? null : v.trim() })}
          />
          <TextField
            label="Customer Ref Date"
            type="date"
            value={header.customer_reference_date}
            onCommit={(v) => patch({ customer_reference_date: v === '' ? null : v })}
          />
          {/*
            What the customer will be QUOTED in. Set here, once, and carried into the quotation
            when it is built — so the quote page inherits a decision instead of guessing one.
          */}
          <LinkField
            label="Customer Currency"
            value={header.customer_currency ?? defaultCurrency}
            useOptions={useCurrencyPicker}
            placeholder="Search currencies…"
            hint={header.customer_currency === null ? `default from ${defaultCurrencySource} — change if the customer is quoted otherwise` : undefined}
            onCommit={(customer_currency) => patch({ customer_currency })}
          />
          {/* Enquiry Type sits directly above Department because Department DEPENDS on it:
              `masters.get_customer_departments?enquiry_type=` returns a different list per type
              (Provision -> Food, Grocery…; Technical -> BATTERIES, Bridge…; Commercial and
              Bonded Stores -> none). Reading top-to-bottom now matches the order you have to
              fill them in. */}
          <SelectField
            label="Enquiry Type"
            value={header.enquiry_type}
            options={WRITABLE_ENQUIRY_TYPES}
            onCommit={(v) => patch({ enquiry_type: v as (typeof WRITABLE_ENQUIRY_TYPES)[number] })}
          />

          {/*
            ENABLED 2026-09-07. This was disabled for a month on a measurement that was simply
            wrong: "Enquiry has NO department field — 42 fields, none matching". That query read
            `DocField`, which holds only STANDARD fields. The department is a CUSTOM one, and
            Frappe keeps those in a different doctype entirely:

                Custom Field  dt=Enquiry  fieldname=custom_customer_department
                              fieldtype=Link  options=Customer Department

            The write that "was accepted and silently discarded" used the wrong NAME too —
            `customer_department`, which is the stock-ERPNext `Opportunity` field. With the real
            name it round-trips: verified end to end on ENQ-2026-00027, `update_enquiry` then
            `get_enquiry` reads back `BATTERIES`.

            The options were always real and correctly filtered by enquiry type
            (`masters.get_customer_departments`) — only the storage was thought to be missing.

            SCOPE is unchanged, and still deliberate: the department belongs on the ENQUIRY and the
            CUSTOMER QUOTATION, never on an RFQ or anything else supplier-facing. A supplier has no
            use for which of the customer's departments asked, and it would leak the customer's
            internal structure onto a document they receive.
          */}
          <SelectField
            label="Department"
            value={header.department}
            options={departments.data ?? []}
            hint={
              departments.data && departments.data.length === 0
                ? `No departments defined for ${header.enquiry_type}`
                : undefined
            }
            onCommit={(department) => patch({ department })}
          />
          <LinkField
            label="Sales Person"
            value={header.sales_person}
            useOptions={useScopedSalesPeople}
            placeholder={
              header.customer && header.company
                ? 'Search sales people for this customer…'
                : 'Search sales people…'
            }
            onCommit={(sales_person) => patch({ sales_person })}
          />

          {/* Field pairing follows the prototype's grid order (lines 766-776). */}
          <LinkField
            label="Delivery Location"
            value={header.delivery_location ?? ''}
            useOptions={useDeliveryLocationPicker}
            placeholder="Search delivery locations…"
            guessed={wasGuessed('delivery_location')}
            onCommit={(delivery_location) => {
              confirm('delivery_location')
              // The location decides the selling price list, so the two are written together —
              // `null` when the location has none, never a stale list from the previous one.
              patchLogistics({ delivery_location, price_list: priceLists.data?.get(delivery_location) ?? null })
            }}
            tag={shownPriceList ? `price list: ${shownPriceList}` : undefined}
          />
          <TextField
            label="Type"
            value={header.type}
            hint="Read-only — reflects how the enquiry was created."
          />

          {/*
            The stored value is the CODE (`Vessel` is named by it), so the name is resolved and
            shown beside it — `Vessel: 1002898` on its own is not something anyone can check.
            The code stays visible because it is what the enquiry actually holds and what the
            customer's own paperwork quotes.
          */}
          <LinkField
            label="Vessel"
            value={header.vessel ?? ''}
            useOptions={useVesselPicker}
            placeholder="Search by vessel name…"
            hint={vesselName.data ? `${vesselName.data} · code ${header.vessel}` : undefined}
            onCommit={(vessel) => patchLogistics({ vessel })}
          />

          {isManager ? (
            /*
              A REAL `User` picker, not a hardcoded list.
              This was a `<select>` over `KNOWN_PURCHASERS = ['Akhil','Sana','Rahul','Manoj']` —
              seed-data names from docs/DATA-MODEL.md, none of which is a Frappe user. It was
              unreachable while `role` was hardwired to 'purchaser'; the moment `SessionProvider`
              started reading the real role from `auth.get_current_user` (which returns
              `purchase_manager` for the signed-in user), this branch went live and every option in
              it would have failed with `User 'Sana' not found` — the same class of bug that
              `User 'Akhil' not found` was on enquiry create.
            */
            <label className="ef">
              <span>Purchaser</span>
              <LinkCombo
                value={header.purchaser}
                onChange={(purchaser) => assignPurchaser.mutate(purchaser)}
                useOptions={usePurchaserPicker}
                placeholder="Search users by name…"
                disabled={assignPurchaser.isPending}
              />
            </label>
          ) : (
            <TextField label="Purchaser" value={header.purchaser} hint="Only a purchase manager can reassign." />
          )}
          <LinkField
            label="Port of Delivery"
            value={header.delivery_port ?? ''}
            useOptions={usePortPicker}
            placeholder="Search ports…"
            onCommit={(delivery_port) => patchLogistics({ delivery_port })}
          />
        </div>
      ) : null}
    </div>
  )
}
